import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LinkedinLogo, TwitterLogo, GithubLogo, DribbbleLogo } from "@phosphor-icons/react";
import { BRAND, NAV_LINKS, SERVICES } from "../lib/constants";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="bg-black text-white relative overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand */}
          <div className="md:col-span-5">
            <p className="overline mb-6">{BRAND.short} • Est. {BRAND.founded}</p>
            <h3 className="font-display text-3xl md:text-4xl leading-tight tracking-tight max-w-md">
              Let's build something{" "}
              <span className="text-[#d4af37] italic">worth keeping.</span>
            </h3>
            <Link
              to="/contact"
              data-testid="footer-cta"
              className="inline-flex items-center gap-3 mt-8 border-b border-[#d4af37] text-[#d4af37] pb-1 hover:gap-5 transition-all duration-300"
            >
              Start a conversation
              <span>→</span>
            </Link>
          </div>

          {/* Navigation */}
          <div className="md:col-span-2">
            <p className="overline mb-5">Navigate</p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-nav-${l.label.toLowerCase()}`}
                    className="text-[#a0abc0] hover:text-white transition-colors text-sm"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="md:col-span-3">
            <p className="overline mb-5">Services</p>
            <ul className="space-y-3">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.id}>
                  <Link
                    to="/services"
                    className="text-[#a0abc0] hover:text-white transition-colors text-sm"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-2">
            <p className="overline mb-5">Studio</p>
            <address className="not-italic text-sm text-[#a0abc0] space-y-3">
              <p>{BRAND.address}</p>
              <p>
                <a
                  href={`mailto:${BRAND.email}`}
                  className="hover:text-white transition-colors"
                >
                  {BRAND.email}
                </a>
              </p>
            </address>
          </div>
        </div>

        {/* Massive brand name */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2 }}
          className="mt-24 mb-8"
        >
          <p
            className="font-display tracking-tighter leading-none text-[#0f1424] select-none"
            style={{
              fontSize: "clamp(80px, 18vw, 280px)",
              fontWeight: 600,
              backgroundImage:
                "linear-gradient(180deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.02) 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            EVO TECHNOLOGY
          </p>
        </motion.div>

        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <p className="text-xs text-[#64748b] font-mono">
            © {year} {BRAND.name}. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <a
              href={BRAND.social.linkedin}
              data-testid="social-linkedin"
              aria-label="LinkedIn"
              className="text-[#a0abc0] hover:text-[#d4af37] transition-colors"
            >
              <LinkedinLogo size={20} />
            </a>
            <a
              href={BRAND.social.twitter}
              data-testid="social-twitter"
              aria-label="Twitter"
              className="text-[#a0abc0] hover:text-[#d4af37] transition-colors"
            >
              <TwitterLogo size={20} />
            </a>
            <a
              href={BRAND.social.github}
              data-testid="social-github"
              aria-label="GitHub"
              className="text-[#a0abc0] hover:text-[#d4af37] transition-colors"
            >
              <GithubLogo size={20} />
            </a>
            <a
              href={BRAND.social.dribbble}
              data-testid="social-dribbble"
              aria-label="Dribbble"
              className="text-[#a0abc0] hover:text-[#d4af37] transition-colors"
            >
              <DribbbleLogo size={20} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
